import jsPDF from 'jspdf';
import type { KitResponse, SelectedProduct } from '@nfa/types';

interface PDFExportOptions {
  includeCheckboxes?: boolean;
  includeAlternatives?: boolean;
  includeWeather?: boolean;
}

/**
 * Format a location string for display
 * Converts snake_case to Title Case (e.g., "tour_du_mont_blanc" → "Tour du Mont Blanc")
 */
function formatLocation(location: string | null): string | null {
  if (!location) return null;

  return location
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Generate a PDF checklist from a kit response
 */
export function generateKitPDF(
  kitResponse: KitResponse,
  options: PDFExportOptions = {}
): jsPDF {
  const {
    includeCheckboxes = true,
    includeAlternatives = false,
    includeWeather = true,
  } = options;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  let y = 20;
  const leftMargin = 20;
  const pageWidth = 210; // A4 width in mm
  const contentWidth = pageWidth - 2 * leftMargin;

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Hiking Kit Checklist', leftMargin, y);
  y += 12;

  // Trip summary
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const { inference, meta } = kitResponse;

  const locationText = formatLocation(inference.location.specific_route) ||
    formatLocation(inference.location.region) ||
    inference.location.country.charAt(0).toUpperCase() + inference.location.country.slice(1);
  doc.text(`Location: ${locationText}`, leftMargin, y);
  y += 5;

  doc.text(`Duration: ${inference.duration_days} days`, leftMargin, y);
  y += 5;

  doc.text(`Month: ${inference.timing.month}`, leftMargin, y);
  y += 5;

  doc.text(`Style: ${inference.style} | Budget: ${inference.budget_tier}`, leftMargin, y);
  y += 5;

  // Weather info if available
  if (includeWeather && meta.weather) {
    const w = meta.weather;
    doc.text(
      `Weather: ${w.avg_low_c}-${w.avg_high_c}°C, ${w.avg_rainfall_mm}mm rain (${w.rainy_days} days)`,
      leftMargin,
      y
    );
    y += 5;
  }

  y += 5;

  // Horizontal line
  doc.setDrawColor(200, 200, 200);
  doc.line(leftMargin, y, pageWidth - leftMargin, y);
  y += 8;

  // Kit items
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Kit Items', leftMargin, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  for (const item of kitResponse.kit.items) {
    // Check if we need a new page
    if (y > 270) {
      doc.addPage();
      y = 20;
    }

    y = renderKitItem(doc, item, leftMargin, y, contentWidth, includeCheckboxes);

    // Render alternatives if requested
    if (includeAlternatives && item.alternatives && item.alternatives.length > 0) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      for (const alt of item.alternatives.slice(0, 2)) {
        const diffPrice = alt.diff_price > 0 ? `+£${alt.diff_price}` : `£${alt.diff_price}`;
        const diffWeight = alt.diff_weight > 0 ? `+${alt.diff_weight}g` : `${alt.diff_weight}g`;
        doc.text(
          `    Alt: ${alt.product_name} (${diffPrice}, ${diffWeight})`,
          leftMargin + 8,
          y
        );
        y += 4;
      }
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
    }

    y += 2;
  }

  y += 5;

  // Totals section
  if (y > 250) {
    doc.addPage();
    y = 20;
  }

  doc.setDrawColor(200, 200, 200);
  doc.line(leftMargin, y, pageWidth - leftMargin, y);
  y += 8;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Totals', leftMargin, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const { totals } = kitResponse.kit;
  doc.text(`Total Items: ${totals.item_count}`, leftMargin, y);
  y += 5;

  doc.text(`Total Weight: ${totals.weight_kg}kg (${totals.weight_g}g)`, leftMargin, y);
  y += 5;

  doc.text(`Total Cost: £${totals.price_gbp}`, leftMargin, y);
  y += 10;

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Generated: ${new Date(meta.generated_at).toLocaleDateString('en-GB')} | nfa.kit`,
    leftMargin,
    285
  );

  return doc;
}

/**
 * Render a single kit item row
 */
function renderKitItem(
  doc: jsPDF,
  item: SelectedProduct,
  x: number,
  y: number,
  _width: number,
  includeCheckbox: boolean
): number {
  const checkboxSize = 4;
  let currentX = x;

  if (includeCheckbox) {
    // Draw checkbox
    doc.setDrawColor(0, 0, 0);
    doc.rect(currentX, y - 3, checkboxSize, checkboxSize);
    currentX += checkboxSize + 3;
  }

  // Category name (bold)
  doc.setFont('helvetica', 'bold');
  doc.text(`${item.category_name}:`, currentX, y);
  currentX += doc.getTextWidth(`${item.category_name}: `) + 1;

  // Product name
  doc.setFont('helvetica', 'normal');
  doc.text(item.product_name, currentX, y);

  // Price and weight on the right
  const priceWeight = `£${item.price_gbp} | ${item.weight_g}g`;
  const priceWeightWidth = doc.getTextWidth(priceWeight);
  doc.setTextColor(100, 100, 100);
  doc.text(priceWeight, 190 - priceWeightWidth, y);
  doc.setTextColor(0, 0, 0);

  return y + 5;
}

/**
 * Download the PDF with a generated filename
 */
export function downloadKitPDF(
  kitResponse: KitResponse,
  options?: PDFExportOptions
): void {
  const pdf = generateKitPDF(kitResponse, options);
  const location = formatLocation(kitResponse.inference.location.specific_route) ||
    formatLocation(kitResponse.inference.location.region) ||
    kitResponse.inference.location.country.charAt(0).toUpperCase() + kitResponse.inference.location.country.slice(1);

  const filename = `kit-${location}-${kitResponse.inference.timing.month}-${Date.now()}.pdf`;
  pdf.save(filename.toLowerCase().replace(/\s+/g, '-'));
}

/**
 * Get the PDF as a blob for other uses (e.g., sharing)
 */
export function getKitPDFBlob(
  kitResponse: KitResponse,
  options?: PDFExportOptions
): Blob {
  const pdf = generateKitPDF(kitResponse, options);
  return pdf.output('blob');
}
